"use client";

import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { ThemeId, ThemeConfig, THEMES, DEFAULT_THEME, getThemeById } from "./themes";

export interface ThemeContextType {
  theme: ThemeConfig;
  themeId: ThemeId;
  themes: ThemeConfig[];
  setTheme: (id: ThemeId) => void;
  isLight: boolean;
  isDark: boolean;
}

const ThemeContext = createContext<ThemeContextType | null>(null);

const STORAGE_KEY = "kubehost_theme";

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [themeId, setThemeIdState] = useState<ThemeId>(DEFAULT_THEME);
  const [, setMounted] = useState(false);

  // Apply theme attributes and variables to document
  const applyThemeToDOM = useCallback((id: ThemeId) => {
    if (typeof document === "undefined") return;
    const config = getThemeById(id);
    const root = document.documentElement;

    root.setAttribute("data-theme", id);
    root.setAttribute("data-theme-mode", config.mode);

    // Apply all CSS variables directly for absolute consistency
    if (config.variables) {
      Object.entries(config.variables).forEach(([key, val]) => {
        root.style.setProperty(key, val);
      });
    }

    if (config.mode === "light") {
      root.classList.remove("dark");
      root.classList.add("light");
    } else {
      root.classList.remove("light");
      root.classList.add("dark");
    }
  }, []);

  // Initialize theme from localStorage or existing DOM attribute on mount
  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY) as ThemeId | null;
      const domTheme = document.documentElement.getAttribute("data-theme") as ThemeId | null;
      const initial = stored || domTheme || DEFAULT_THEME;

      const validated = getThemeById(initial).id;
      setThemeIdState(validated);
      applyThemeToDOM(validated);
    } catch {
      applyThemeToDOM(DEFAULT_THEME);
    } finally {
      setMounted(true);
    }
  }, [applyThemeToDOM]);

  const setTheme = useCallback(
    (newThemeId: ThemeId) => {
      const validated = getThemeById(newThemeId).id;
      setThemeIdState(validated);
      applyThemeToDOM(validated);

      try {
        localStorage.setItem(STORAGE_KEY, validated);
      } catch {
        // Ignore localStorage write failures in private browsing
      }

      // Proactively notify user settings endpoint if logged in (fire & forget)
      try {
        fetch("/api/user/preferences", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ theme: validated }),
        }).catch(() => {});
      } catch {
        // Ignore API failures
      }
    },
    [applyThemeToDOM]
  );

  const themeConfig = getThemeById(themeId);

  return (
    <ThemeContext.Provider
      value={{
        theme: themeConfig,
        themeId,
        themes: THEMES,
        setTheme,
        isLight: themeConfig.mode === "light",
        isDark: themeConfig.mode === "dark",
      }}
    >
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme(): ThemeContextType {
  const context = useContext(ThemeContext);
  if (!context) {
    // Graceful fallback if invoked outside Provider
    const fallback = getThemeById(DEFAULT_THEME);
    return {
      theme: fallback,
      themeId: DEFAULT_THEME,
      themes: THEMES,
      setTheme: () => {},
      isLight: fallback.mode === "light",
      isDark: fallback.mode === "dark",
    };
  }
  return context;
}
